import {HotelOfferEntity} from '@findhotel/sapi'

import {calculateTotalRate} from '../offer/business/calculateTotalRate'
import {OldHotelOfferEntity} from '../offer/business/offersMapping'
import {sortOffersByPrice} from '../offer/utils/sortOffers'
import {SearchData, SearchState} from '../sapi/services/searchApi'
import {getLengthOfStay} from '../utils/date'

/**
 * Get the prices for the top offer of a hotel
 * @param params - The parameters object
 * @param params.offerEntity - The hotel offer entity to get the prices for
 * @param params.checkIn - The check-in date
 * @param params.checkOut - The check-out date
 * @param params.includeTaxes - Whether to include taxes
 * @param params.includeLocalTaxes - Whether to include local taxes
 * @returns The price and anchor price for the hotel offer entity
 */
export const getHotelPrices = ({
  offerEntity,
  checkIn,
  checkOut,
  includeTaxes,
  includeLocalTaxes
}: {
  offerEntity: HotelOfferEntity | OldHotelOfferEntity | undefined
  checkIn: string
  checkOut: string
  includeTaxes: boolean
  includeLocalTaxes: boolean
}) => {
  if (!offerEntity || !offerEntity?.offers?.length)
    return {price: undefined, anchorPrice: undefined}

  const {offers} = offerEntity
  const firstOffer = offers[0]

  const sortedOffers = sortOffersByPrice(offerEntity.offers)
  const mostExpensiveOffer = sortedOffers[sortedOffers.length - 1]

  const lengthOfStay = getLengthOfStay(checkIn, checkOut)

  const anchorPrice = mostExpensiveOffer?.rate
    ? calculateTotalRate(
        mostExpensiveOffer?.rate,
        includeTaxes,
        includeLocalTaxes
      ) / lengthOfStay
    : undefined

  const price =
    calculateTotalRate(firstOffer.rate, includeTaxes, includeLocalTaxes) /
    lengthOfStay

  return {price, anchorPrice}
}

/**
 * Get the cashback for a hotel offer entity
 * @param offerEntity - The hotel offer entity to get the cashback for
 * @returns The cashback for the hotel offer entity
 */
export const getCashback = (
  offerEntity: HotelOfferEntity | OldHotelOfferEntity | undefined
) => {
  if (!offerEntity || !offerEntity?.offers?.length) return {cashback: undefined}

  const {offers} = offerEntity
  const firstOffer = offers[0]

  const {cashback} = firstOffer

  return {cashback}
}

/**
 * Check if the anchor is complete
 * @param data - The search data to check
 * @returns True if the anchor is complete, false otherwise
 */
export const isAnchorComplete = (data?: SearchData) =>
  data?.status?.anchorComplete

/**
 * Check if the hotels (not offers) are received
 * @param data - The search data to check
 * @returns True if the hotels (not offers) are received, false otherwise
 */
export const isHotelsReceived = (data?: SearchData) =>
  data?.searchState &&
  [SearchState.OffersPending, SearchState.Completed].includes(data.searchState)

/**
 * Check if the search is complete
 * @param data - The search data to check
 * @returns True if the search is complete, false otherwise
 */
export const isComplete = (data?: SearchData) =>
  data?.searchState === SearchState.Completed
